<template>
  <div :class="`tw-conf--${config.public.brand}`">
    <NuxtLayout :name="layout">
      <div
        class="flex flex-col h-full text-white bg-primary-900 dahl:bg-gradient-to-r dahl:from-primary-700 dahl:to-primary-900"
        :class="{ 'server-error': !isServerInitiated }"
      >
        <div v-if="!isServerInitiated" class="m-6 md:m-20 w-[90px] md:w-[125px]">
          <img
            :src="`/brand/${config.public.brand}/logo-error.svg`"
            :alt="`${config.public.brand} logo`"
            :title="config.public.brand"
          />
        </div>
        <div class="flex flex-col">
          <div class="m-6 md:m-20">
            <p class="mb-4 md:text-xl">{{ error.statusCode }}</p>
            <h1 class="mb-8 text-2xl font-bold md:text-6xl">{{ message.headline }}</h1>
            <p class="mb-8 text-sm md:text-xl">{{ message.reason }}</p>
            <a v-if="!isServerInitiated" href="/" class="inline-block">
              <CIconButton name="arrow_forward" variant="secondary" @click="navigate">
                <template #default>
                  <span class="mr-2">{{ t('to_startpage') }}</span>
                </template>
              </CIconButton>
            </a>

            <CIconButton v-else name="arrow_back" variant="secondary" @click="navigate">
              <template #right>
                <span class="ml-2">{{ t('go_back') }}</span>
              </template>
            </CIconButton>
          </div>
        </div>
      </div>
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
import fonts from '@dahl-js/dahl-ui/styles/vars/fonts.js';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useServerStore } from '@/stores';

const { error } = defineProps<{ error: { statusCode: number } }>();

const config = useRuntimeConfig();
const router = useRouter();
const { t } = useI18n();
const { isServerInitiated } = storeToRefs(useServerStore());

const layout = computed(() => (isServerInitiated.value ? 'default' : 'plain'));

const navigate = () => {
  router.go(-1);
};

const message = computed(() => {
  const message = {
    headline: t('errors.apologies'),
    reason: t('errors.try_again_later'),
  };
  switch (error.statusCode) {
    case 404:
      message.headline = t('errors.not_found');
      message.reason = t('errors.not_found_reason');
      break;
    case 500:
      message.headline = t('errors.internal_server_error');
      message.reason = t('errors.try_again_later');
      break;
  }
  return message;
});

const fontLinks = fonts[config.public.brand].flatMap(({ url }) => [
  {
    rel: 'preload',
    as: 'style',
    href: url,
  },
  {
    rel: 'stylesheet',
    href: url,
  },
]);

if (!config.public.isTest) {
  useHead({
    link: [
      {
        type: 'text/css',
        rel: 'stylesheet',
        href: `/${config.public.brand}.css`,
      },
      ...fontLinks,
    ],
  });
}
</script>

<script lang="ts">
export default defineComponent({
  name: 'ErrorPage',
});
</script>
